import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { comparisonsAPI } from '../lib/api';
import AppHeader from '../components/AppHeader';
import GuidedViewComparison from '../components/GuidedViewComparison';
import TutorialOverlay, { useTutorial, HelpMenuButton } from '../components/TutorialOverlay';
import TrialUpgradeModal from '../components/TrialUpgradeModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Upload, Loader2, FileText, Trash2, Download, 
  ZoomIn, ZoomOut, RotateCcw, RotateCw, Layers, Eye, AlertCircle,
  CheckCircle2, XCircle, MinusCircle, X, Save, Undo2, 
  Maximize2, Move, Edit2, MousePointer, Circle, ChevronDown, Play, Settings2
} from 'lucide-react';
import Footer from '../components/Footer';

// SmartRevision Tutorial Steps - Will be generated with translations inside component
const getTutorialSteps = (t) => [
  {
    targetSelector: '[data-testid="upload-drawing-a"]',
    title: t('tutorial_upload_old'),
    description: t('tutorial_upload_old_desc'),
    icon: <Upload className="w-5 h-5 text-blue-600" />
  },
  {
    targetSelector: '[data-testid="upload-drawing-b"]',
    title: t('tutorial_upload_new'),
    description: t('tutorial_upload_new_desc'),
    icon: <Upload className="w-5 h-5 text-blue-600" />
  },
  {
    targetSelector: '[data-testid="analyze-btn"]',
    title: t('tutorial_start_analysis'),
    description: t('tutorial_start_analysis_desc'),
    icon: <Play className="w-5 h-5 text-blue-600" />
  },
  {
    targetSelector: '[data-testid="open-editor-btn"]',
    title: t('tutorial_open_editor'),
    description: t('tutorial_open_editor_desc'),
    icon: <Settings2 className="w-5 h-5 text-blue-600" />
  }
];

// ============================================
// FULLSCREEN DIFFERENCE EDITOR COMPONENT
// ============================================
const FullscreenEditor = ({ 
  isOpen, 
  onClose, 
  comparison, 
  onExportPDF,
  exportingPDF,
  drawingAName,
  drawingBName,
  onCirclesChange,  // Callback to notify parent of circle changes
  onSaveMarkers,    // Callback to save markers to DB
  fullOverlay,      // Full resolution overlay (lazy loaded)
  loadingOverlay    // Loading state for full overlay
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // State
  const [blendOpacity, setBlendOpacity] = useState(50); // 0 = A only, 100 = B only
  const [zoom, setZoom] = useState(100);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [initialZoomCalculated, setInitialZoomCalculated] = useState(false);
  
  // Images
  const [imageA, setImageA] = useState(null);
  const [imageB, setImageB] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  
  // Overlay image (full resolution)
  const [overlayImage, setOverlayImage] = useState(null);
  
  // Circles/ellipses data from differences
  const [circles, setCircles] = useState([]);
  
  // Text notes
  const [notes, setNotes] = useState([]);
  
  // Guided comparison state
  const [isGuided, setIsGuided] = useState(false);
  const [guidedViews, setGuidedViews] = useState([]);
  const [viewImages, setViewImages] = useState({ A: [], B: [] }); // Cropped view images
  
  // Selection and editing state - unified for circles and notes
  const [selectedCircleIndex, setSelectedCircleIndex] = useState(null);
  const [selectedNoteIndex, setSelectedNoteIndex] = useState(null);
  const [isDraggingCircle, setIsDraggingCircle] = useState(false);
  const [isDraggingNote, setIsDraggingNote] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Resize handle dragging state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null); // 'tl', 'tr', 'bl', 'br'
  const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [resizeStartCircle, setResizeStartCircle] = useState(null);
  const [resizeStartNote, setResizeStartNote] = useState(null);
  const [resizingNote, setResizingNote] = useState(false);
  
  // Note text editing
  const [editingNoteText, setEditingNoteText] = useState(false);
  const noteInputRef = useRef(null);
  
  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Undo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Load full overlay image when it becomes available
  useEffect(() => {
    if (fullOverlay && isOpen) {
      const img = new Image();
      img.onload = () => {
        setOverlayImage(img);
      };
      img.src = `data:image/png;base64,${fullOverlay}`;
    }
  }, [fullOverlay, isOpen]);
  
  // Save state to history (both circles and notes)
  const saveToHistory = useCallback((newCircles, newNotes) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({
      circles: JSON.parse(JSON.stringify(newCircles)),
      notes: JSON.parse(JSON.stringify(newNotes || notes))
    });
    // Keep only last 20 states
    if (newHistory.length > 20) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setHasChanges(true); // Mark as having unsaved changes
  }, [history, historyIndex, notes]);
  
  // Save markers and notes to database
  const handleSaveMarkers = useCallback(async () => {
    if (!onSaveMarkers || saving) return;
    setSaving(true);
    try {
      await onSaveMarkers(circles, notes);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  }, [circles, notes, onSaveMarkers, saving]);
  
  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const state = history[newIndex];
      if (state.circles) {
        setCircles(JSON.parse(JSON.stringify(state.circles)));
      }
      if (state.notes) {
        setNotes(JSON.parse(JSON.stringify(state.notes)));
      }
      setSelectedCircleIndex(null);
      setSelectedNoteIndex(null);
    }
  }, [historyIndex, history]);
  
  // Track deleted circles (mark as userDeleted instead of removing)
  const [deletedCircleIds, setDeletedCircleIds] = useState(new Set());
  
  // State for adding new circle
  const [showAddCircleMenu, setShowAddCircleMenu] = useState(false);
  
  // Delete selected circle or note
  const handleDeleteSelected = useCallback(() => {
    if (selectedCircleIndex !== null) {
      const circle = circles[selectedCircleIndex];
      // Mark as deleted by user instead of removing (for PDF tracking)
      const newCircles = circles.map((c, i) => 
        i === selectedCircleIndex 
          ? { ...c, userDeleted: true }
          : c
      );
      setCircles(newCircles);
      saveToHistory(newCircles, notes);
      setSelectedCircleIndex(null);
    } else if (selectedNoteIndex !== null) {
      const newNotes = notes.filter((_, i) => i !== selectedNoteIndex);
      setNotes(newNotes);
      saveToHistory(circles, newNotes);
      setSelectedNoteIndex(null);
    }
  }, [selectedCircleIndex, selectedNoteIndex, circles, notes, saveToHistory]);
  
  // Calculate the center of current viewport in image coordinates (0-1)
  const getViewportCenter = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return { x: 0.5, y: 0.5 }; // Fallback to center
    }
    
    const imgWidth = imageA?.width || imageB?.width || 800;
    const imgHeight = imageA?.height || imageB?.height || 600;
    
    // Canvas center in canvas coordinates
    const canvasCenterX = canvas.width / 2;
    const canvasCenterY = canvas.height / 2;
    
    // Convert canvas center to image coordinates (0-1)
    // Inverse of the transform: translate(pan.x + canvas.width/2, pan.y + canvas.height/2) then scale(zoom/100)
    const imgX = ((canvasCenterX - canvas.width / 2 - pan.x) / (zoom / 100) + imgWidth / 2) / imgWidth;
    const imgY = ((canvasCenterY - canvas.height / 2 - pan.y) / (zoom / 100) + imgHeight / 2) / imgHeight;
    
    // Clamp to valid range with some margin for the element size
    return {
      x: Math.max(0.1, Math.min(0.9, imgX)),
      y: Math.max(0.1, Math.min(0.9, imgY))
    };
  }, [imageA, imageB, pan, zoom]);

  // Add new circle with specified type/color
  const handleAddCircle = useCallback((circleType) => {
    const center = getViewportCenter();
    const newCircle = {
      id: Date.now(),
      x: center.x - 0.06,  // Offset by half width to center the circle
      y: center.y - 0.06,  // Offset by half height to center the circle
      width: 0.12,
      height: 0.12,
      type: circleType,  // 'aggiunto', 'rimosso', 'modificato'
      userAdded: true,   // Flag to indicate user added this
      description: circleType === 'aggiunto' ? 'Aggiunto (utente)' 
                  : circleType === 'rimosso' ? 'Rimosso (utente)'
                  : 'Modificato (utente)'
    };
    const newCircles = [...circles, newCircle];
    setCircles(newCircles);
    saveToHistory(newCircles, notes);
    setSelectedCircleIndex(newCircles.length - 1);
    setSelectedNoteIndex(null);
    setShowAddCircleMenu(false);
  }, [circles, notes, saveToHistory, getViewportCenter]);
  
  // Add new note
  const handleAddNote = useCallback(() => {
    const center = getViewportCenter();
    const newNote = {
      id: Date.now(),
      x: center.x - 0.075,  // Offset by half width to center the note
      y: center.y - 0.025,  // Offset by half height to center the note
      width: 0.15,
      height: 0.05,
      text: 'Nuova nota',
      fontSize: 12
    };
    const newNotes = [...notes, newNote];
    setNotes(newNotes);
    saveToHistory(circles, newNotes);
    setSelectedNoteIndex(newNotes.length - 1);
    setSelectedCircleIndex(null);
    setEditingNoteText(true);
  }, [notes, circles, saveToHistory, getViewportCenter]);
  
  // Load images and circles data
  useEffect(() => {
    if (!isOpen || !comparison) return;
    
    setImagesLoaded(false);
    setInitialZoomCalculated(false);
    
    let loadedCount = 0;
    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount >= 2) {
        setImagesLoaded(true);
      }
    };
    
    // Load Drawing A
    if (comparison.drawing_a_base64) {
      const imgA = new Image();
      imgA.onload = () => { setImageA(imgA); checkAllLoaded(); };
      imgA.onerror = () => checkAllLoaded();
      imgA.src = `data:image/png;base64,${comparison.drawing_a_base64}`;
    } else {
      checkAllLoaded();
    }
    
    // Load Drawing B
    if (comparison.drawing_b_base64) {
      const imgB = new Image();
      imgB.onload = () => { setImageB(imgB); checkAllLoaded(); };
      imgB.onerror = () => checkAllLoaded();
      imgB.src = `data:image/png;base64,${comparison.drawing_b_base64}`;
    } else {
      checkAllLoaded();
    }
    
    // Extract circles data from differences_json
    if (comparison.differences_json) {
      try {
        const diffs = typeof comparison.differences_json === 'string' 
          ? JSON.parse(comparison.differences_json) 
          : comparison.differences_json;
        
        if (diffs.visual && Array.isArray(diffs.visual)) {
          const initialCircles = diffs.visual.map((v, i) => ({ ...v, id: i }));
          setCircles(initialCircles);
          
          // Load notes if present
          const initialNotes = diffs.notes && Array.isArray(diffs.notes) 
            ? diffs.notes.map((n, i) => ({ ...n, id: i }))
            : [];
          setNotes(initialNotes);
          
          // Initialize history with first state
          setHistory([{ 
            circles: JSON.parse(JSON.stringify(initialCircles)),
            notes: JSON.parse(JSON.stringify(initialNotes))
          }]);
          setHistoryIndex(0);
        }
        
        // Check if this is a guided comparison
        const guided = comparison.analysis_method === 'guided' || 
                      (diffs.guided_views && Array.isArray(diffs.guided_views));
        setIsGuided(guided);
        
        if (guided && diffs.guided_views) {
          setGuidedViews(diffs.guided_views);
        }
      } catch (e) {
        console.error('Error parsing differences:', e);
      }
    }
  }, [isOpen, comparison]);
  
  // Notify parent when circles or notes change
  useEffect(() => {
    if (onCirclesChange && (circles.length > 0 || notes.length > 0)) {
      onCirclesChange(circles, notes);
    }
  }, [circles, notes, onCirclesChange]);
  
  // Extract cropped view images for guided comparison
  useEffect(() => {
    if (!isGuided || !imagesLoaded || !imageA || !imageB || guidedViews.length === 0) return;
    
    // Create offscreen canvases to extract view regions
    const extractView = (img, rect) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const x = rect.x * img.width;
      const y = rect.y * img.height;
      const w = rect.w * img.width;
      const h = rect.h * img.height;
      
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
      
      const extractedImg = new Image();
      extractedImg.src = canvas.toDataURL('image/png');
      return { img: extractedImg, rect, width: w, height: h };
    };
    
    const viewsA = guidedViews.map(v => extractView(imageA, v.rectA));
    const viewsB = guidedViews.map(v => extractView(imageB, v.rectB));
    
    setViewImages({ A: viewsA, B: viewsB });
  }, [isGuided, imagesLoaded, imageA, imageB, guidedViews]);
  
  // Calculate initial zoom to fit all (after images loaded and container ready)
  useEffect(() => {
    if (!imagesLoaded || !containerRef.current || initialZoomCalculated) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    // Get image dimensions
    const imgWidth = imageA?.width || imageB?.width || 800;
    const imgHeight = imageA?.height || imageB?.height || 600;
    
    // Calculate zoom to fit with some padding
    const padding = 40;
    const zoomX = (containerWidth - padding * 2) / imgWidth * 100;
    const zoomY = (containerHeight - padding * 2) / imgHeight * 100;
    const fitZoom = Math.min(zoomX, zoomY, 100); // Cap at 100%
    
    setZoom(Math.max(10, Math.round(fitZoom)));
    setPan({ x: 0, y: 0 });
    setInitialZoomCalculated(true);
  }, [imagesLoaded, imageA, imageB, initialZoomCalculated]);
  
  // Close add circle menu when clicking outside
  useEffect(() => {
    if (!showAddCircleMenu) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.add-circle-menu-container')) {
        setShowAddCircleMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddCircleMenu]);
  
  // Keyboard handler for close, delete, and undo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        setSelectedCircleIndex(null);
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteSelected();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleDeleteSelected, handleUndo]);
  
  // Render canvas - blend A and B, then draw circles on top
  useEffect(() => {
    if (!isOpen || !canvasRef.current || !containerRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    
    // Set canvas size to match container
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    
    // Clear with dark background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // For GUIDED comparison: show views in their positions on B
    if (isGuided && viewImages.B.length > 0 && imageB) {
      // Use image B dimensions as reference
      const imgWidth = imageB.width;
      const imgHeight = imageB.height;
      
      // Apply transformations
      ctx.save();
      ctx.translate(pan.x + canvas.width / 2, pan.y + canvas.height / 2);
      ctx.scale(zoom / 100, zoom / 100);
      
      // Calculate image position (centered)
      const x = -imgWidth / 2;
      const y = -imgHeight / 2;
      
      // Draw Drawing B as base background (always visible)
      ctx.globalAlpha = 1;
      ctx.drawImage(imageB, x, y, imgWidth, imgHeight);
      
      // Draw view overlays from A - these show the OLD version at the positions defined on B
      // The opacity slider controls how visible these overlays are
      const overlayOpacity = 1 - (blendOpacity / 100);
      
      if (overlayOpacity > 0.05) {
        guidedViews.forEach((view, idx) => {
          if (viewImages.A[idx]?.img?.complete && view.rectB) {
            const rectB = view.rectB;
            const vx = x + rectB.x * imgWidth;
            const vy = y + rectB.y * imgHeight;
            const vw = rectB.w * imgWidth;
            const vh = rectB.h * imgHeight;
            
            // Draw semi-transparent background for the view area
            ctx.globalAlpha = overlayOpacity * 0.3;
            ctx.fillStyle = '#a855f7';
            ctx.fillRect(vx, vy, vw, vh);
            
            // Draw the cropped view from A at the position/size defined on B
            ctx.globalAlpha = overlayOpacity * 0.7;
            ctx.drawImage(viewImages.A[idx].img, vx, vy, vw, vh);
            
            // Draw view label
            ctx.globalAlpha = 1;
            ctx.fillStyle = '#8b5cf6';
            ctx.font = 'bold 14px Arial';
            const label = view.name || `Vista ${idx + 1}`;
            const textWidth = ctx.measureText(label).width;
            ctx.fillRect(vx, vy - 24, textWidth + 12, 22);
            ctx.fillStyle = '#ffffff';
            ctx.fillText(label, vx + 6, vy - 10);
          }
        });
      }
      
      ctx.globalAlpha = 1;
    } else {
      // STANDARD comparison: blend A and B as before
      // Get image dimensions
      const imgWidth = imageA?.width || imageB?.width || 800;
      const imgHeight = imageA?.height || imageB?.height || 600;
      
      // Apply transformations
      ctx.save();
      ctx.translate(pan.x + canvas.width / 2, pan.y + canvas.height / 2);
      ctx.scale(zoom / 100, zoom / 100);
      
      // Calculate image position (centered)
      const x = -imgWidth / 2;
      const y = -imgHeight / 2;
      
      // Draw Drawing A (Vecchia Revisione) - visible when slider is left
      if (imageA) {
        ctx.globalAlpha = 1 - (blendOpacity / 100);
        ctx.drawImage(imageA, x, y, imgWidth, imgHeight);
      }
      
      // Draw Drawing B (Nuova Revisione) - visible when slider is right
      if (imageB) {
        ctx.globalAlpha = blendOpacity / 100;
        ctx.drawImage(imageB, x, y, imgWidth, imgHeight);
      }
    }
    
    // Reset alpha for circles
    ctx.globalAlpha = 1;
    
    // Get image dimensions for circle positioning
    const imgWidth = isGuided && imageB ? imageB.width : (imageA?.width || imageB?.width || 800);
    const imgHeight = isGuided && imageB ? imageB.height : (imageA?.height || imageB?.height || 600);
    const x = -imgWidth / 2;
    const y = -imgHeight / 2;
    
    // Draw circles/ellipses on top (always visible)
    // Note: coordinates in differences_json are in PERCENTAGE (0-1 range)
    circles.forEach((circle, index) => {
      // Skip deleted circles
      if (circle.userDeleted) return;
      
      // Convert percentage to pixels
      const circleX = (circle.x || 0) * imgWidth;
      const circleY = (circle.y || 0) * imgHeight;
      const circleWidth = (circle.width || 0.05) * imgWidth;
      const circleHeight = (circle.height || 0.05) * imgHeight;
      
      const cx = x + circleX + circleWidth / 2;
      const cy = y + circleY + circleHeight / 2;
      const rx = Math.max(circleWidth / 2, 10);
      const ry = Math.max(circleHeight / 2, 10);
      
      const isSelected = index === selectedCircleIndex;
      
      // Set color based on type
      let strokeColor = '#eab308'; // Yellow for modified (default)
      if (circle.type === 'rimosso' || circle.type === 'removed') {
        strokeColor = '#ef4444'; // Red
      } else if (circle.type === 'aggiunto' || circle.type === 'added') {
        strokeColor = '#22c55e'; // Green
      }
      
      // Draw selection glow if selected
      if (isSelected) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx + 2, ry + 2, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = isSelected ? 4 : 3;
      
      // Use dashed line for user-added or user-modified circles
      if (circle.userAdded || circle.userModified) {
        ctx.setLineDash([8, 4]);
      } else {
        ctx.setLineDash([]);
      }
      
      // Draw ellipse
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
      
      // Reset line dash
      ctx.setLineDash([]);
      
      // Draw "U" badge for user-added circles
      if (circle.userAdded) {
        ctx.fillStyle = strokeColor;
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('U', cx, cy - ry - 10);
      }
      
      // Draw resize handles if selected
      if (isSelected) {
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        
        // Four corner handles
        const handles = [
          { hx: cx - rx, hy: cy - ry },
          { hx: cx + rx, hy: cy - ry },
          { hx: cx - rx, hy: cy + ry },
          { hx: cx + rx, hy: cy + ry }
        ];
        
        handles.forEach(h => {
          ctx.beginPath();
          ctx.rect(h.hx - 4, h.hy - 4, 8, 8);
          ctx.fill();
          ctx.stroke();
        });
      }
    });
    
    // Draw notes on top
    notes.forEach((note, index) => {
      const noteX = (note.x || 0) * imgWidth;
      const noteY = (note.y || 0) * imgHeight;
      const noteWidth = (note.width || 0.1) * imgWidth;
      const noteHeight = (note.height || 0.05) * imgHeight;
      
      const nx = x + noteX;
      const ny = y + noteY;
      
      const isSelected = index === selectedNoteIndex;
      
      // Draw note background - light blue with high opacity for print readability
      ctx.fillStyle = isSelected ? 'rgba(219, 234, 254, 0.98)' : 'rgba(219, 234, 254, 0.95)';
      ctx.strokeStyle = isSelected ? '#1e40af' : '#3b82f6';
      ctx.lineWidth = isSelected ? 3 : 2;
      
      // Rounded rectangle for note
      const radius = 4;
      ctx.beginPath();
      ctx.moveTo(nx + radius, ny);
      ctx.lineTo(nx + noteWidth - radius, ny);
      ctx.quadraticCurveTo(nx + noteWidth, ny, nx + noteWidth, ny + radius);
      ctx.lineTo(nx + noteWidth, ny + noteHeight - radius);
      ctx.quadraticCurveTo(nx + noteWidth, ny + noteHeight, nx + noteWidth - radius, ny + noteHeight);
      ctx.lineTo(nx + radius, ny + noteHeight);
      ctx.quadraticCurveTo(nx, ny + noteHeight, nx, ny + noteHeight - radius);
      ctx.lineTo(nx, ny + radius);
      ctx.quadraticCurveTo(nx, ny, nx + radius, ny);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Draw note text - dark blue for maximum contrast on light background
      ctx.fillStyle = '#1e3a5f';
      const fontSize = Math.max(12, Math.min(noteHeight * 0.6, 24));
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Truncate text if too long
      const text = note.text || 'Nota';
      const maxTextWidth = noteWidth - 10;
      let displayText = text;
      while (ctx.measureText(displayText).width > maxTextWidth && displayText.length > 3) {
        displayText = displayText.slice(0, -4) + '...';
      }
      ctx.fillText(displayText, nx + noteWidth / 2, ny + noteHeight / 2);
      
      // Draw resize handles if selected
      if (isSelected) {
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        
        const handles = [
          { hx: nx, hy: ny },
          { hx: nx + noteWidth, hy: ny },
          { hx: nx, hy: ny + noteHeight },
          { hx: nx + noteWidth, hy: ny + noteHeight }
        ];
        
        handles.forEach(h => {
          ctx.beginPath();
          ctx.rect(h.hx - 4, h.hy - 4, 8, 8);
          ctx.fill();
          ctx.stroke();
        });
      }
    });
    
    ctx.restore();
  }, [isOpen, imageA, imageB, circles, notes, blendOpacity, zoom, pan, selectedCircleIndex, selectedNoteIndex, isGuided, viewImages, guidedViews]);
  
  // Convert screen coordinates to image coordinates
  const screenToImageCoords = useCallback((screenX, screenY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = screenX - rect.left;
    const canvasY = screenY - rect.top;
    
    const imgWidth = imageA?.width || imageB?.width || 800;
    const imgHeight = imageA?.height || imageB?.height || 600;
    
    // Reverse the transformations
    const imgX = ((canvasX - canvas.width / 2 - pan.x) / (zoom / 100) + imgWidth / 2) / imgWidth;
    const imgY = ((canvasY - canvas.height / 2 - pan.y) / (zoom / 100) + imgHeight / 2) / imgHeight;
    
    return { x: imgX, y: imgY };
  }, [pan, zoom, imageA, imageB]);
  
  // Check if point is inside a circle
  const findCircleAtPoint = useCallback((imgX, imgY) => {
    for (let i = circles.length - 1; i >= 0; i--) {
      const circle = circles[i];
      const cx = (circle.x || 0) + (circle.width || 0.05) / 2;
      const cy = (circle.y || 0) + (circle.height || 0.05) / 2;
      const rx = (circle.width || 0.05) / 2;
      const ry = (circle.height || 0.05) / 2;
      
      // Check if point is inside ellipse
      const dx = (imgX - cx) / rx;
      const dy = (imgY - cy) / ry;
      if (dx * dx + dy * dy <= 1.5) { // 1.5 for some tolerance
        return i;
      }
    }
    return null;
  }, [circles]);
  
  // Check if point is on a resize handle of the selected circle
  const findResizeHandle = useCallback((imgX, imgY) => {
    if (selectedCircleIndex === null) return null;
    
    const circle = circles[selectedCircleIndex];
    if (!circle) return null;
    
    const x = circle.x || 0;
    const y = circle.y || 0;
    const w = circle.width || 0.05;
    const h = circle.height || 0.05;
    
    // Handle positions (corners of the bounding box)
    const handles = {
      'tl': { hx: x, hy: y },
      'tr': { hx: x + w, hy: y },
      'bl': { hx: x, hy: y + h },
      'br': { hx: x + w, hy: y + h }
    };
    
    // Tolerance in percentage (relative to image size)
    const tolerance = 0.015; // 1.5% of image
    
    for (const [handle, pos] of Object.entries(handles)) {
      if (Math.abs(imgX - pos.hx) < tolerance && Math.abs(imgY - pos.hy) < tolerance) {
        return handle;
      }
    }
    return null;
  }, [selectedCircleIndex, circles]);
  
  // Check if point is inside a note
  const findNoteAtPoint = useCallback((imgX, imgY) => {
    for (let i = notes.length - 1; i >= 0; i--) {
      const note = notes[i];
      const x = note.x || 0;
      const y = note.y || 0;
      const w = note.width || 0.1;
      const h = note.height || 0.05;
      
      if (imgX >= x && imgX <= x + w && imgY >= y && imgY <= y + h) {
        return i;
      }
    }
    return null;
  }, [notes]);
  
  // Check if point is on a resize handle of the selected note
  const findNoteResizeHandle = useCallback((imgX, imgY) => {
    if (selectedNoteIndex === null) return null;
    
    const note = notes[selectedNoteIndex];
    if (!note) return null;
    
    const x = note.x || 0;
    const y = note.y || 0;
    const w = note.width || 0.1;
    const h = note.height || 0.05;
    
    const handles = {
      'tl': { hx: x, hy: y },
      'tr': { hx: x + w, hy: y },
      'bl': { hx: x, hy: y + h },
      'br': { hx: x + w, hy: y + h }
    };
    
    const tolerance = 0.015;
    
    for (const [handle, pos] of Object.entries(handles)) {
      if (Math.abs(imgX - pos.hx) < tolerance && Math.abs(imgY - pos.hy) < tolerance) {
        return handle;
      }
    }
    return null;
  }, [selectedNoteIndex, notes]);
  
  // Mouse handlers
  const handleMouseDown = (e) => {
    // Don't start drag if clicking on note input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    
    const imgCoords = screenToImageCoords(e.clientX, e.clientY);
    
    // First check if clicking on a resize handle of selected circle
    const circleHandle = findResizeHandle(imgCoords.x, imgCoords.y);
    if (circleHandle && selectedCircleIndex !== null) {
      setIsResizing(true);
      setResizingNote(false);
      setResizeHandle(circleHandle);
      setResizeStartPos(imgCoords);
      setResizeStartCircle({ ...circles[selectedCircleIndex] });
      return;
    }
    
    // Check if clicking on a resize handle of selected note
    const noteHandle = findNoteResizeHandle(imgCoords.x, imgCoords.y);
    if (noteHandle && selectedNoteIndex !== null) {
      setIsResizing(true);
      setResizingNote(true);
      setResizeHandle(noteHandle);
      setResizeStartPos(imgCoords);
      setResizeStartNote({ ...notes[selectedNoteIndex] });
      return;
    }
    
    // Check if clicking on a note (notes have priority over circles)
    const noteIndex = findNoteAtPoint(imgCoords.x, imgCoords.y);
    if (noteIndex !== null) {
      setSelectedNoteIndex(noteIndex);
      setSelectedCircleIndex(null);
      setIsDraggingNote(true);
      const note = notes[noteIndex];
      setDragOffset({
        x: imgCoords.x - (note.x || 0),
        y: imgCoords.y - (note.y || 0)
      });
      return;
    }
    
    // Then check if clicking on a circle
    const circleIndex = findCircleAtPoint(imgCoords.x, imgCoords.y);
    
    if (circleIndex !== null) {
      // Clicked on a circle - select it and prepare for dragging
      setSelectedCircleIndex(circleIndex);
      setSelectedNoteIndex(null);
      setIsDraggingCircle(true);
      const circle = circles[circleIndex];
      setDragOffset({
        x: imgCoords.x - (circle.x || 0),
        y: imgCoords.y - (circle.y || 0)
      });
    } else {
      // Clicked on empty space - start panning
      setSelectedCircleIndex(null);
      setSelectedNoteIndex(null);
      setEditingNoteText(false);
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };
  
  const handleMouseMove = (e) => {
    const imgCoords = screenToImageCoords(e.clientX, e.clientY);
    
    // Handle resizing note
    if (isResizing && resizingNote && selectedNoteIndex !== null && resizeStartNote) {
      const deltaX = imgCoords.x - resizeStartPos.x;
      const deltaY = imgCoords.y - resizeStartPos.y;
      
      const newNotes = [...notes];
      let newX = resizeStartNote.x || 0;
      let newY = resizeStartNote.y || 0;
      let newW = resizeStartNote.width || 0.1;
      let newH = resizeStartNote.height || 0.05;
      
      const minSize = 0.03;
      
      switch (resizeHandle) {
        case 'tl':
          newX = Math.min(resizeStartNote.x + resizeStartNote.width - minSize, resizeStartNote.x + deltaX);
          newY = Math.min(resizeStartNote.y + resizeStartNote.height - minSize, resizeStartNote.y + deltaY);
          newW = Math.max(minSize, resizeStartNote.width - deltaX);
          newH = Math.max(minSize, resizeStartNote.height - deltaY);
          break;
        case 'tr':
          newY = Math.min(resizeStartNote.y + resizeStartNote.height - minSize, resizeStartNote.y + deltaY);
          newW = Math.max(minSize, resizeStartNote.width + deltaX);
          newH = Math.max(minSize, resizeStartNote.height - deltaY);
          break;
        case 'bl':
          newX = Math.min(resizeStartNote.x + resizeStartNote.width - minSize, resizeStartNote.x + deltaX);
          newW = Math.max(minSize, resizeStartNote.width - deltaX);
          newH = Math.max(minSize, resizeStartNote.height + deltaY);
          break;
        case 'br':
          newW = Math.max(minSize, resizeStartNote.width + deltaX);
          newH = Math.max(minSize, resizeStartNote.height + deltaY);
          break;
        default:
          break;
      }
      
      newNotes[selectedNoteIndex] = {
        ...newNotes[selectedNoteIndex],
        x: newX,
        y: newY,
        width: newW,
        height: newH
      };
      setNotes(newNotes);
      return;
    }
    
    // Handle resizing circle
    if (isResizing && !resizingNote && selectedCircleIndex !== null && resizeStartCircle) {
      const deltaX = imgCoords.x - resizeStartPos.x;
      const deltaY = imgCoords.y - resizeStartPos.y;
      
      const newCircles = [...circles];
      let newX = resizeStartCircle.x || 0;
      let newY = resizeStartCircle.y || 0;
      let newW = resizeStartCircle.width || 0.05;
      let newH = resizeStartCircle.height || 0.05;
      
      const minSize = 0.02; // Minimum size 2% of image
      
      // Adjust based on which handle is being dragged
      switch (resizeHandle) {
        case 'tl': // Top-left: adjust x, y, width, height
          newX = Math.min(resizeStartCircle.x + resizeStartCircle.width - minSize, resizeStartCircle.x + deltaX);
          newY = Math.min(resizeStartCircle.y + resizeStartCircle.height - minSize, resizeStartCircle.y + deltaY);
          newW = Math.max(minSize, resizeStartCircle.width - deltaX);
          newH = Math.max(minSize, resizeStartCircle.height - deltaY);
          break;
        case 'tr': // Top-right: adjust y, width, height
          newY = Math.min(resizeStartCircle.y + resizeStartCircle.height - minSize, resizeStartCircle.y + deltaY);
          newW = Math.max(minSize, resizeStartCircle.width + deltaX);
          newH = Math.max(minSize, resizeStartCircle.height - deltaY);
          break;
        case 'bl': // Bottom-left: adjust x, width, height
          newX = Math.min(resizeStartCircle.x + resizeStartCircle.width - minSize, resizeStartCircle.x + deltaX);
          newW = Math.max(minSize, resizeStartCircle.width - deltaX);
          newH = Math.max(minSize, resizeStartCircle.height + deltaY);
          break;
        case 'br': // Bottom-right: adjust width, height only
          newW = Math.max(minSize, resizeStartCircle.width + deltaX);
          newH = Math.max(minSize, resizeStartCircle.height + deltaY);
          break;
        default:
          break;
      }
      
      newCircles[selectedCircleIndex] = {
        ...newCircles[selectedCircleIndex],
        x: newX,
        y: newY,
        width: newW,
        height: newH
      };
      setCircles(newCircles);
      return;
    }
    
    // Handle dragging note
    if (isDraggingNote && selectedNoteIndex !== null) {
      const newNotes = [...notes];
      newNotes[selectedNoteIndex] = {
        ...newNotes[selectedNoteIndex],
        x: imgCoords.x - dragOffset.x,
        y: imgCoords.y - dragOffset.y
      };
      setNotes(newNotes);
      return;
    }
    
    // Handle dragging circle
    if (isDraggingCircle && selectedCircleIndex !== null) {
      const newCircles = [...circles];
      newCircles[selectedCircleIndex] = {
        ...newCircles[selectedCircleIndex],
        x: imgCoords.x - dragOffset.x,
        y: imgCoords.y - dragOffset.y
      };
      setCircles(newCircles);
    } else if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };
  
  const handleMouseUp = () => {
    // Mark circle as user-modified if it was dragged or resized
    if ((isDraggingCircle || (isResizing && !resizingNote)) && selectedCircleIndex !== null) {
      const circle = circles[selectedCircleIndex];
      // Only mark as modified if it's not already a user-added circle
      if (!circle.userAdded && !circle.userModified) {
        const newCircles = [...circles];
        newCircles[selectedCircleIndex] = {
          ...newCircles[selectedCircleIndex],
          userModified: true
        };
        setCircles(newCircles);
        saveToHistory(newCircles, notes);
      } else {
        saveToHistory(circles, notes);
      }
    } else if (isDraggingNote || (isResizing && resizingNote)) {
      saveToHistory(circles, notes);
    }
    
    setIsDraggingCircle(false);
    setIsDraggingNote(false);
    setIsPanning(false);
    setIsResizing(false);
    setResizingNote(false);
    setResizeHandle(null);
  };
  
  // Wheel for zoom - centered on mouse position (like SmartBalloon workspace)
  const handleWheel = (e) => {
    e.preventDefault();
    
    // Zoom centered on mouse position
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Smooth zoom factor (0.9 for zoom out, 1.1 for zoom in)
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(10, Math.min(300, zoom * zoomFactor));
    
    // Current center offset
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    // Point in image space under mouse (before zoom)
    const imageX = (mouseX - centerX - pan.x) / (zoom / 100);
    const imageY = (mouseY - centerY - pan.y) / (zoom / 100);
    
    // Calculate new pan to keep that point under the mouse
    const newPanX = mouseX - centerX - imageX * (newZoom / 100);
    const newPanY = mouseY - centerY - imageY * (newZoom / 100);
    
    setZoom(Math.round(newZoom));
    setPan({ x: newPanX, y: newPanY });
  };
  
  // Fit to screen
  const handleFitToScreen = () => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    const imgWidth = imageA?.width || imageB?.width || 800;
    const imgHeight = imageA?.height || imageB?.height || 600;
    
    const padding = 40;
    const zoomX = (containerWidth - padding * 2) / imgWidth * 100;
    const zoomY = (containerHeight - padding * 2) / imgHeight * 100;
    const fitZoom = Math.min(zoomX, zoomY, 100);
    
    setZoom(Math.max(10, Math.round(fitZoom)));
    setPan({ x: 0, y: 0 });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 flex flex-col">
      {/* Top toolbar */}
      <div className="h-14 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-semibold">{t('diff_editor')}</h2>
          
          {/* Add note button */}
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleAddNote}
            className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
          >
            <FileText className="w-4 h-4 mr-1" />
            {t('add_note')}
          </Button>
          
          {/* Add circle button with color dropdown */}
          <div className="relative add-circle-menu-container">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowAddCircleMenu(!showAddCircleMenu)}
              className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/30"
            >
              <Circle className="w-4 h-4 mr-1" />
              {t('add_circle')}
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
            
            {/* Color selection dropdown */}
            {showAddCircleMenu && (
              <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-xl border border-slate-600 py-1 z-50 min-w-[160px]">
                <button
                  onClick={() => handleAddCircle('aggiunto')}
                  className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-600 text-white text-sm"
                >
                  <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-green-400"></div>
                  {t('green_added')}
                </button>
                <button
                  onClick={() => handleAddCircle('rimosso')}
                  className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-600 text-white text-sm"
                >
                  <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-red-400"></div>
                  {t('red_removed')}
                </button>
                <button
                  onClick={() => handleAddCircle('modificato')}
                  className="w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-slate-600 text-white text-sm"
                >
                  <div className="w-4 h-4 rounded-full bg-yellow-500 border-2 border-yellow-400"></div>
                  {t('yellow_modified')}
                </button>
              </div>
            )}
          </div>
          
          {/* Undo button */}
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-50"
          >
            <Undo2 className="w-4 h-4 mr-1" />
            {t('undo')}
          </Button>
          
          {/* Delete selected circle button */}
          {selectedCircleIndex !== null && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleDeleteSelected}
              className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {t('delete_circle')}
            </Button>
          )}
          
          {/* Delete selected note button */}
          {selectedNoteIndex !== null && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleDeleteSelected}
              className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {t('delete_circle')}
            </Button>
          )}
        </div>
        
        {/* Right side buttons */}
        <div className="flex items-center gap-3">
          {/* Save changes button */}
          <Button
            onClick={handleSaveMarkers}
            disabled={saving || !hasChanges}
            variant="outline"
            className={`border-green-500 text-green-400 hover:bg-green-900/30 hover:text-green-300 ${hasChanges ? 'animate-pulse' : ''}`}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {hasChanges ? t('save_changes') : t('saved')}
          </Button>
          
          <Button
            onClick={() => onExportPDF(circles, notes)}
            disabled={exportingPDF}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6"
          >
            {exportingPDF ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {t('export_pdf')}
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onClose}
            className="text-slate-300 hover:text-white hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Main canvas area */}
      <div 
        ref={containerRef}
        className="flex-1 relative overflow-hidden"
      >
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />
        
        {/* Loading overlay when fetching full resolution */}
        {loadingOverlay && (
          <div className="absolute inset-0 bg-slate-900/80 flex flex-col items-center justify-center z-10">
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mb-4" />
            <p className="text-white text-lg font-medium">{t('loading_high_res')}</p>
            <p className="text-slate-400 text-sm mt-2">{t('loading_high_res_desc')}</p>
          </div>
        )}
        
        {/* Instructions overlay */}
        <div className="absolute bottom-4 left-4 bg-slate-800/80 backdrop-blur-sm text-slate-300 text-xs p-3 rounded-lg">
          <p>{t('editor_instructions_line1')}</p>
          <p>{t('editor_instructions_line2')}</p>
        </div>
        
        {/* Note text editor - appears when a note is selected */}
        {selectedNoteIndex !== null && notes[selectedNoteIndex] && (
          <div className="absolute top-4 left-4 bg-slate-800/95 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-slate-600">
            <label className="text-slate-300 text-xs font-medium mb-2 block">{t('note_text_label')}</label>
            <input
              ref={noteInputRef}
              type="text"
              value={notes[selectedNoteIndex]?.text || ''}
              onChange={(e) => {
                const newNotes = [...notes];
                newNotes[selectedNoteIndex] = {
                  ...newNotes[selectedNoteIndex],
                  text: e.target.value
                };
                setNotes(newNotes);
                setHasChanges(true);
              }}
              onBlur={() => {
                saveToHistory(circles, notes);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
                e.stopPropagation(); // Prevent DEL from deleting note while typing
              }}
              className="bg-slate-700 text-white px-3 py-2 rounded border border-slate-500 focus:border-blue-500 focus:outline-none w-64"
              placeholder="Inserisci testo..."
              autoFocus
            />
          </div>
        )}
      </div>
      
      {/* Bottom controls bar */}
      <div className="h-20 bg-slate-800 border-t border-slate-700 flex items-center px-6 gap-8">
        {/* Blend A/B slider */}
        <div className="flex items-center gap-4 flex-1 max-w-md">
          <span className="text-sm text-blue-400 font-medium w-32 truncate" title={drawingAName}>
            {drawingAName}
          </span>
          <Slider
            value={[blendOpacity]}
            onValueChange={(v) => setBlendOpacity(v[0])}
            min={0}
            max={100}
            step={1}
            className="flex-1"
          />
          <span className="text-sm text-amber-400 font-medium w-32 text-right truncate" title={drawingBName}>
            {drawingBName}
          </span>
        </div>
        
        {/* Zoom controls */}
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setZoom(z => Math.max(10, z - 25))}
            className="text-slate-300 hover:text-white"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-slate-300 w-14 text-center">{zoom}%</span>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setZoom(z => Math.min(300, z + 25))}
            className="text-slate-300 hover:text-white"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleFitToScreen}
            className="text-slate-300 hover:text-white ml-2"
          >
            <Maximize2 className="w-4 h-4 mr-1" />
            {t('fit_to_screen')}
          </Button>
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 border-2 border-red-500 rounded-full" />
            <span className="text-slate-400">{t('removed')}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 border-2 border-green-500 rounded-full" />
            <span className="text-slate-400">{t('added')}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 border-2 border-yellow-500 rounded-full" />
            <span className="text-slate-400">{t('modified')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPARE PAGE COMPONENT
// ============================================
const ComparePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  
  // Tutorial system
  const { shouldShow: shouldShowTutorial, resetTutorial } = useTutorial('smartrevision_intro', token);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStartStep, setTutorialStartStep] = useState(0);
  
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadingA, setUploadingA] = useState(false);
  const [uploadingB, setUploadingB] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  
  // View controls
  const [opacity, setOpacity] = useState(100);
  
  // Fullscreen editor
  const [editorOpen, setEditorOpen] = useState(false);
  
  // Modified circles state (user edits in fullscreen editor)
  const [modifiedCircles, setModifiedCircles] = useState(null);
  
  // Full overlay (lazy loaded for fullscreen editor)
  const [fullOverlay, setFullOverlay] = useState(null);
  const [loadingFullOverlay, setLoadingFullOverlay] = useState(false);
  
  // Modified circles and notes state (user edits in fullscreen editor)
  const [modifiedNotes, setModifiedNotes] = useState(null);
  
  // Editable drawing names - Use translations
  const [drawingAName, setDrawingAName] = useState(null);
  const [drawingBName, setDrawingBName] = useState(null);
  const [editingA, setEditingA] = useState(false);
  const [editingB, setEditingB] = useState(false);
  
  // Set default names after translation is available
  useEffect(() => {
    if (drawingAName === null) setDrawingAName(t('old_revision'));
    if (drawingBName === null) setDrawingBName(t('new_revision'));
  }, [t, drawingAName, drawingBName]);
  
  // Export PDF confirm modal
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  
  // Zero differences dialog
  const [showZeroDiffDialog, setShowZeroDiffDialog] = useState(false);
  
  // Guided view comparison
  const [analyzingGuided, setAnalyzingGuided] = useState(false);
  const [guidedResults, setGuidedResults] = useState(null);
  const [forceExpandGuided, setForceExpandGuided] = useState(false);
  
  // Format mismatch dialog
  const [showFormatMismatchDialog, setShowFormatMismatchDialog] = useState(false);
  const [pendingAnalysisType, setPendingAnalysisType] = useState(null); // 'auto' or 'guided'
  const [pendingGuidedViews, setPendingGuidedViews] = useState(null);
  
  // Trial upgrade modal state
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialModalType, setTrialModalType] = useState('trial_export_blocked');
  
  // Calculate available AI credits
  const plan = user?.plan?.toUpperCase() || 'FREE';
  const isTrial = plan === 'AI_TRIAL';
  const aiUsageCount = user?.ai_usage_count || 0;
  const aiLimit = plan === 'AI' ? 50 : ((plan === 'AI_UNLIMITED' || plan === 'AI_TRIAL') ? 999999 : 0);
  const availableCredits = Math.max(0, aiLimit - aiUsageCount);
  
  // Show tutorial after page loads - but start from appropriate step based on current state
  useEffect(() => {
    if (shouldShowTutorial && !loading && comparison) {
      // Determine which step to start from based on current state
      let startStep = 0;
      
      if (comparison.drawing_a_base64) {
        startStep = 1; // Drawing A loaded, go to step 2 (upload B)
      }
      if (comparison.drawing_a_base64 && comparison.drawing_b_base64) {
        startStep = 2; // Both drawings loaded, go to step 3 (analyze)
      }
      if (comparison.drawing_a_base64 && comparison.drawing_b_base64 && comparison.differences) {
        startStep = 3; // Analysis done, go to step 4 (open editor)
      }
      
      // If we're past all tutorial steps, don't show it
      if (startStep >= getTutorialSteps(t).length) {
        return;
      }
      
      // Set the starting step before showing
      setTutorialStartStep(startStep);
      const timer = setTimeout(() => setShowTutorial(true), 800);
      return () => clearTimeout(timer);
    }
  }, [shouldShowTutorial, loading, comparison]);
  
  // Load comparison
  useEffect(() => {
    loadComparison();
  }, [id]);
  
  const loadComparison = async () => {
    try {
      setLoading(true);
      const response = await comparisonsAPI.get(id);
      setComparison(response.data);
      // Set custom names if available, otherwise use new defaults
      if (response.data.drawing_a_custom_name) {
        setDrawingAName(response.data.drawing_a_custom_name);
      }
      if (response.data.drawing_b_custom_name) {
        setDrawingBName(response.data.drawing_b_custom_name);
      }
    } catch (error) {
      toast.error('Errore nel caricamento della comparazione');
      navigate('/revision');
    } finally {
      setLoading(false);
    }
  };
  
  // Open fullscreen editor and lazy load full overlay
  const handleOpenEditor = async () => {
    setEditorOpen(true);
    
    // Only load full overlay if we don't have it yet and comparison has one
    if (!fullOverlay && comparison?.has_full_overlay) {
      setLoadingFullOverlay(true);
      try {
        const response = await comparisonsAPI.getFullOverlay(id);
        setFullOverlay(response.data.overlay_base64);
      } catch (error) {
        console.error('Error loading full overlay:', error);
        toast.error('Errore nel caricamento dell\'immagine ad alta risoluzione');
      } finally {
        setLoadingFullOverlay(false);
      }
    }
  };
  
  // Upload handlers
  const handleUploadA = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    
    setUploadingA(true);
    try {
      await comparisonsAPI.uploadDrawing(id, 'A', file);
      toast.success(`${drawingAName} caricato`);
      loadComparison();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingA(false);
    }
  };
  
  const handleUploadB = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    
    setUploadingB(true);
    try {
      await comparisonsAPI.uploadDrawing(id, 'B', file);
      toast.success(`${drawingBName} caricato`);
      loadComparison();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingB(false);
    }
  };
  
  // Rotate drawing 90° clockwise
  const [rotatingA, setRotatingA] = useState(false);
  const [rotatingB, setRotatingB] = useState(false);
  
  const rotateImage = (base64Data, callback) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Swap width and height for 90° rotation
      canvas.width = img.height;
      canvas.height = img.width;
      
      // Move to center, rotate 90° clockwise, then draw
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2); // 90° clockwise
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      
      // Get the rotated image as base64 (keep as PNG for quality)
      const rotatedBase64 = canvas.toDataURL('image/png').split(',')[1];
      callback(rotatedBase64);
    };
    img.src = `data:image/png;base64,${base64Data}`;
  };
  
  const handleRotateA = async () => {
    if (!comparison?.drawing_a_base64 || rotatingA) return;
    
    setRotatingA(true);
    try {
      rotateImage(comparison.drawing_a_base64, async (rotatedBase64) => {
        try {
          await comparisonsAPI.updateDrawing(id, 'A', rotatedBase64);
          toast.success(`${drawingAName} ruotato di 90°`);
          loadComparison();
        } catch (error) {
          toast.error('Errore nella rotazione');
        } finally {
          setRotatingA(false);
        }
      });
    } catch (error) {
      toast.error('Errore nella rotazione');
      setRotatingA(false);
    }
  };
  
  const handleRotateB = async () => {
    if (!comparison?.drawing_b_base64 || rotatingB) return;
    
    setRotatingB(true);
    try {
      rotateImage(comparison.drawing_b_base64, async (rotatedBase64) => {
        try {
          await comparisonsAPI.updateDrawing(id, 'B', rotatedBase64);
          toast.success(`${drawingBName} ruotato di 90°`);
          loadComparison();
        } catch (error) {
          toast.error('Errore nella rotazione');
        } finally {
          setRotatingB(false);
        }
      });
    } catch (error) {
      toast.error('Errore nella rotazione');
      setRotatingB(false);
    }
  };
  
  // Analyze differences
  const handleAnalyze = async (resizeToMatch = false) => {
    if (!comparison?.drawing_a_base64 || !comparison?.drawing_b_base64) {
      toast.error(t('upload_both_drawings'));
      return;
    }
    
    // Check for format mismatch - show dialog if not already confirmed
    if (comparison?.format_mismatch && !resizeToMatch && !showFormatMismatchDialog) {
      setPendingAnalysisType('auto');
      setShowFormatMismatchDialog(true);
      return;
    }
    
    setAnalyzing(true);
    try {
      // Pass user's language to backend for localized AI responses
      const userLang = localStorage.getItem('language') || 'en';
      const response = await comparisonsAPI.analyze(id, { 
        alignment_method: 'auto', 
        language: userLang,
        resize_to_match: resizeToMatch 
      });
      
      const totalDifferences = response.data.differences?.stats?.total || 0;
      
      // Show zero differences dialog if no differences found
      if (totalDifferences === 0) {
        setShowZeroDiffDialog(true);
      } else {
        toast.success(`${t('status_completed')}! ${t('differences_found')} ${totalDifferences}`);
      }
      
      loadComparison();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Export PDF - shows confirm first
  const handleExportPDFClick = () => {
    // Trial users cannot export - show upgrade modal
    if (isTrial) {
      console.log('[Export] Trial user - showing upgrade modal');
      setTrialModalType('trial_export_blocked');
      setTrialModalOpen(true);
      return;
    }
    setShowExportConfirm(true);
  };
  
  // Wrapper for editor export - checks trial status first
  const handleEditorExportPDF = (circles, notes) => {
    if (isTrial) {
      console.log('[Export from Editor] Trial user - showing upgrade modal');
      setTrialModalType('trial_export_blocked');
      setTrialModalOpen(true);
      return;
    }
    // Call the actual export
    handleExportPDFConfirm(circles, notes);
  };

  const handleExportPDFConfirm = async (circlesFromEditor = null, notesFromEditor = null) => {
    // Additional check for trial
    if (isTrial) {
      setTrialModalType('trial_export_blocked');
      setTrialModalOpen(true);
      return;
    }
    
    setShowExportConfirm(false);
    setExportingPDF(true);
    try {
      // If called from editor, use provided circles/notes
      // Otherwise, use saved data from database (from comparison.differences_json)
      let markersToExport = circlesFromEditor;
      let notesToExport = notesFromEditor;
      
      console.log('[Export PDF] circlesFromEditor:', circlesFromEditor ? circlesFromEditor.length : 'null');
      console.log('[Export PDF] comparison.differences_json exists:', !!comparison?.differences_json);
      
      // If not from editor, extract saved markers from differences_json
      if (markersToExport === null && comparison?.differences_json) {
        try {
          const diffs = typeof comparison.differences_json === 'string' 
            ? JSON.parse(comparison.differences_json) 
            : comparison.differences_json;
          console.log('[Export PDF] Parsed diffs.visual:', diffs.visual ? diffs.visual.length : 'null');
          console.log('[Export PDF] Parsed diffs.notes:', diffs.notes ? diffs.notes.length : 'null');
          markersToExport = diffs.visual || null;
          notesToExport = diffs.notes || null;
        } catch (e) {
          console.error('Error parsing differences_json:', e);
        }
      }
      
      // Fallback to local state if still null
      if (markersToExport === null) {
        markersToExport = modifiedCircles;
        console.log('[Export PDF] Using modifiedCircles fallback:', modifiedCircles ? modifiedCircles.length : 'null');
      }
      if (notesToExport === null) {
        notesToExport = modifiedNotes;
      }
      
      console.log('[Export PDF] Final markersToExport:', markersToExport ? markersToExport.length : 'null');
      
      const response = await comparisonsAPI.exportPDF(id, markersToExport, notesToExport);
      const blob = response.data;
      
      // Download ZIP file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartrevision_${comparison?.name || 'export'}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toast.success(t('pdf_exported_success') || 'Esportazione completata! ZIP con 2 PDF scaricato.');
      
      // Clean up the URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      // Handle trial export blocked from backend
      if (error.response?.data?.detail === 'trial_export_blocked') {
        setTrialModalType('trial_export_blocked');
        setTrialModalOpen(true);
      } else {
        toast.error('Errore nell\'export PDF');
      }
    } finally {
      setExportingPDF(false);
    }
  };
  
  // Save markers and notes to database
  const handleSaveMarkers = async (circles, notes) => {
    try {
      await comparisonsAPI.updateMarkers(id, circles, notes);
      toast.success('Modifiche salvate');
      // Reload comparison to get updated data
      loadComparison();
    } catch (error) {
      toast.error('Errore nel salvataggio');
      throw error; // Re-throw so the editor knows it failed
    }
  };
  
  // Delete comparison
  const handleDelete = async () => {
    if (!window.confirm('Eliminare questa comparazione?')) return;
    
    try {
      await comparisonsAPI.delete(id);
      toast.success('Comparazione eliminata');
      navigate('/revision');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };
  
  // Guided view comparison analysis
  const handleGuidedAnalysis = async (views, resizeToMatch = false) => {
    // Check for format mismatch - show dialog if not already confirmed
    if (comparison?.format_mismatch && !resizeToMatch && !showFormatMismatchDialog) {
      setPendingAnalysisType('guided');
      setPendingGuidedViews(views);
      setShowFormatMismatchDialog(true);
      return;
    }
    
    setAnalyzingGuided(true);
    setGuidedResults(null);
    
    try {
      const response = await comparisonsAPI.analyzeGuidedViews(id, views, resizeToMatch);
      setGuidedResults(response.data);
      toast.success(`Analisi completata: ${response.data.total_differences} differenze trovate in ${views.length} viste`);
      
      // Update comparison locally with the new data from guided analysis
      if (response.data.overlay_thumbnail || response.data.differences_json) {
        setComparison(prev => ({
          ...prev,
          overlay_thumbnail: response.data.overlay_thumbnail || prev.overlay_thumbnail,
          differences_json: response.data.differences_json || prev.differences_json,
          analysis_status: 'completed'
        }));
      }
      
      // Reload comparison after a short delay to get updated data from DB
      setTimeout(() => loadComparison(), 500);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'analisi guidata';
      toast.error(message);
    } finally {
      setAnalyzingGuided(false);
    }
  };
  
  // Handle format mismatch dialog response
  const handleFormatMismatchConfirm = (shouldResize) => {
    setShowFormatMismatchDialog(false);
    
    if (pendingAnalysisType === 'auto') {
      handleAnalyze(shouldResize);
    } else if (pendingAnalysisType === 'guided' && pendingGuidedViews) {
      handleGuidedAnalysis(pendingGuidedViews, shouldResize);
    }
    
    // Reset pending state
    setPendingAnalysisType(null);
    setPendingGuidedViews(null);
  };
  
  // Dropzone configs
  const dropzoneConfigA = useDropzone({
    onDrop: handleUploadA,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    maxFiles: 1,
    disabled: uploadingA
  });
  
  const dropzoneConfigB = useDropzone({
    onDrop: handleUploadB,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    maxFiles: 1,
    disabled: uploadingB
  });
  
  // Parse differences
  const getDifferences = () => {
    if (!comparison?.differences_json) return null;
    try {
      return typeof comparison.differences_json === 'string' 
        ? JSON.parse(comparison.differences_json) 
        : comparison.differences_json;
    } catch {
      return null;
    }
  };
  
  const differences = getDifferences();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Fullscreen Editor */}
      <FullscreenEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        comparison={comparison}
        onExportPDF={handleEditorExportPDF}
        exportingPDF={exportingPDF}
        drawingAName={drawingAName}
        drawingBName={drawingBName}
        onCirclesChange={(circles, notes) => {
          setModifiedCircles(circles);
          setModifiedNotes(notes);
        }}
        onSaveMarkers={handleSaveMarkers}
        fullOverlay={fullOverlay}
        loadingOverlay={loadingFullOverlay}
      />
      
      {/* Export PDF Confirm Modal */}
      {showExportConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <Download className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Esporta PDF
              </h3>
              <p className="text-slate-600 text-sm mb-2">
                Il PDF verrà aperto in una nuova finestra.
              </p>
              <p className="text-slate-400 text-xs mb-6">
                Verificare il consenso all'apertura dei popup per questo sito
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowExportConfirm(false)}
                >
                  Annulla
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleExportPDFConfirm}
                >
                  Continua
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Zero Differences Dialog */}
      {showZeroDiffDialog && (
        <div className="fixed inset-0 z-[250] bg-black/60 flex items-center justify-center" data-testid="zero-diff-dialog">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {t('zero_differences_title')}
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                {t('zero_differences_message')}
              </p>
              <p className="text-slate-500 text-xs mb-6 italic">
                {t('zero_differences_hint')}
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowZeroDiffDialog(false)}
                >
                  OK
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={() => {
                    setShowZeroDiffDialog(false);
                    // Force expand the guided comparison accordion
                    setForceExpandGuided(true);
                    // Scroll to guided comparison section
                    setTimeout(() => {
                      const guidedSection = document.querySelector('[data-testid="guided-view-comparison"]');
                      if (guidedSection) {
                        guidedSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }}
                >
                  {t('try_guided_comparison')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Format Mismatch Dialog */}
      {showFormatMismatchDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center" data-testid="format-mismatch-dialog">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Formati Disegni Diversi
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                I due disegni hanno dimensioni diverse:
              </p>
              {comparison?.drawing_a_dims && comparison?.drawing_b_dims && (
                <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-blue-600">{drawingAName}:</span>
                    <span className="text-slate-600">{comparison.drawing_a_dims.width} x {comparison.drawing_a_dims.height} px</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-amber-600">{drawingBName}:</span>
                    <span className="text-slate-600">{comparison.drawing_b_dims.width} x {comparison.drawing_b_dims.height} px</span>
                  </div>
                </div>
              )}
              <p className="text-slate-500 text-sm mb-6">
                Vuoi ridimensionare automaticamente i disegni per allinearli?
                <br />
                <span className="text-xs text-slate-400">
                  (Consigliato per coordinate accurate)
                </span>
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => handleFormatMismatchConfirm(false)}
                  data-testid="format-mismatch-no-btn"
                >
                  No, analizza così
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => handleFormatMismatchConfirm(true)}
                  data-testid="format-mismatch-yes-btn"
                >
                  Sì, ridimensiona
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Analysis Loading Banner */}
      {analyzing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 text-center">
            <div className="relative mb-6">
              <div className="w-20 h-20 mx-auto border-4 border-purple-200 rounded-full animate-pulse" />
              <Layers className="w-10 h-10 text-purple-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-bounce" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 mb-2">
              SmartRevision in corso...
            </h3>
            <p className="text-slate-500 mb-4">
              Analisi delle differenze tra i disegni
            </p>
            <div className="space-y-2 text-sm text-slate-600">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                <span>Confronto visivo con OpenCV</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                <span>Analisi semantica con intelligenza artificiale</span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              L'analisi può richiedere 30-60 secondi per disegni complessi
            </p>
          </div>
        </div>
      )}
      
      {/* PDF Export Loading Banner */}
      {exportingPDF && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 text-center">
            <div className="relative mb-6">
              <div className="w-16 h-16 mx-auto border-4 border-blue-200 rounded-full animate-pulse" />
              <Download className="w-8 h-8 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">
              Generazione PDF...
            </h3>
            <p className="text-slate-500 text-sm">
              Il PDF si aprirà in una nuova finestra
            </p>
          </div>
        </div>
      )}
      
      {/* App Header */}
      <AppHeader />
      
      {/* Sub-header for this comparison */}
      <div className="h-14 border-b border-slate-200 bg-white flex items-center px-6 justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/revision')}
            data-testid="back-to-revision"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('all_comparisons')}
          </Button>
          <div className="h-5 w-px bg-slate-300" />
          <div>
            <span className="text-sm font-medium text-slate-700">{comparison?.name || t('new_comparison_name')}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {comparison?.analysis_status === 'completed' && (
            <Button 
              size="lg"
              onClick={handleExportPDFClick}
              disabled={exportingPDF}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 shadow-lg shadow-blue-500/25"
              data-testid="export-pdf-btn"
            >
              {exportingPDF ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  {t('export_pdf')}
                </>
              )}
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleDelete}
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid="delete-comparison-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Drawing A Upload - "Vecchia Revisione" */}
          <Card data-testid="drawing-a-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-blue-500 text-white flex items-center justify-center text-xs font-bold">A</span>
                {editingA ? (
                  <Input
                    value={drawingAName}
                    onChange={(e) => setDrawingAName(e.target.value)}
                    onBlur={() => setEditingA(false)}
                    onKeyDown={(e) => e.key === 'Enter' && setEditingA(false)}
                    className="h-7 w-44 text-base"
                    autoFocus
                  />
                ) : (
                  <span 
                    className="cursor-pointer hover:text-blue-600 flex items-center gap-1"
                    onClick={() => setEditingA(true)}
                  >
                    {drawingAName}
                    <Edit2 className="w-3 h-3 text-slate-400" />
                  </span>
                )}
                {comparison?.is_vector_a && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-auto">{t('vector')}</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparison?.drawing_a_base64 ? (
                <div className="relative">
                  <img 
                    src={`data:image/png;base64,${comparison.drawing_a_base64}`}
                    alt={drawingAName}
                    className="w-full h-48 object-contain bg-white rounded border"
                    data-testid="drawing-a-preview"
                  />
                  <p className="text-xs text-slate-500 mt-2 truncate">
                    {comparison.drawing_a_name}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                    onClick={handleRotateA}
                    disabled={rotatingA}
                    title="Ruota 90° in senso orario"
                  >
                    {rotatingA ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <div
                  {...dropzoneConfigA.getRootProps()}
                  className={`
                    border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                    ${dropzoneConfigA.isDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400'}
                    ${uploadingA ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  data-testid="upload-drawing-a"
                >
                  <input {...dropzoneConfigA.getInputProps()} />
                  {uploadingA ? (
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-600">
                        Trascina {drawingAName} qui
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        PDF o immagine
                      </p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Drawing B Upload - "Nuova Revisione" */}
          <Card data-testid="drawing-b-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="w-6 h-6 rounded bg-amber-500 text-white flex items-center justify-center text-xs font-bold">B</span>
                {editingB ? (
                  <Input
                    value={drawingBName}
                    onChange={(e) => setDrawingBName(e.target.value)}
                    onBlur={() => setEditingB(false)}
                    onKeyDown={(e) => e.key === 'Enter' && setEditingB(false)}
                    className="h-7 w-44 text-base"
                    autoFocus
                  />
                ) : (
                  <span 
                    className="cursor-pointer hover:text-amber-600 flex items-center gap-1"
                    onClick={() => setEditingB(true)}
                  >
                    {drawingBName}
                    <Edit2 className="w-3 h-3 text-slate-400" />
                  </span>
                )}
                {comparison?.is_vector_b && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-auto">{t('vector')}</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparison?.drawing_b_base64 ? (
                <div className="relative">
                  <img 
                    src={`data:image/png;base64,${comparison.drawing_b_base64}`}
                    alt={drawingBName}
                    className="w-full h-48 object-contain bg-white rounded border"
                    data-testid="drawing-b-preview"
                  />
                  <p className="text-xs text-slate-500 mt-2 truncate">
                    {comparison.drawing_b_name}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                    onClick={handleRotateB}
                    disabled={rotatingB}
                    title="Ruota 90° in senso orario"
                  >
                    {rotatingB ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <div
                  {...dropzoneConfigB.getRootProps()}
                  className={`
                    border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                    ${dropzoneConfigB.isDragActive ? 'border-amber-500 bg-amber-50' : 'border-slate-300 hover:border-amber-400'}
                    ${uploadingB ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  data-testid="upload-drawing-b"
                >
                  <input {...dropzoneConfigB.getInputProps()} />
                  {uploadingB ? (
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-600">
                        Trascina {drawingBName} qui
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        PDF o immagine
                      </p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Overlay / Results - Clickable to open fullscreen */}
          <Card 
            data-testid="overlay-card"
            className={(comparison?.overlay_thumbnail || comparison?.has_full_overlay) ? 'cursor-pointer hover:ring-2 hover:ring-purple-400 transition-all' : ''}
            onClick={() => (comparison?.overlay_thumbnail || comparison?.has_full_overlay) && handleOpenEditor()}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="w-5 h-5" />
                {t('differences')}
                {comparison?.analysis_status === 'completed' && differences?.stats && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded ml-auto">
                    {differences.stats.total} {t('found')}
                  </span>
                )}
                {(comparison?.overlay_thumbnail || comparison?.has_full_overlay) && (
                  <Maximize2 className="w-4 h-4 text-purple-500 ml-2" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(comparison?.overlay_thumbnail || comparison?.has_full_overlay) ? (
                <div className="relative">
                  <img 
                    src={`data:image/${comparison.overlay_thumbnail ? 'jpeg' : 'png'};base64,${comparison.overlay_thumbnail || ''}`}
                    alt="Overlay differenze (anteprima)"
                    className="w-full h-48 object-contain bg-white rounded border"
                    style={{ opacity: opacity / 100 }}
                    data-testid="overlay-preview"
                  />
                  
                  {/* Click hint - Open Editor Button */}
                  <div 
                    className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors rounded cursor-pointer"
                    onClick={() => handleOpenEditor()}
                    data-testid="open-editor-btn"
                  >
                    <span className="text-white text-sm font-medium opacity-0 hover:opacity-100 transition-opacity flex items-center gap-2">
                      <Maximize2 className="w-5 h-5" />
                      {t('diff_editor')}
                    </span>
                  </div>
                  
                  {/* Controls */}
                  <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16">{t('opacity')}</span>
                      <Slider
                        value={[opacity]}
                        onValueChange={(v) => setOpacity(v[0])}
                        min={10}
                        max={100}
                        step={5}
                        className="flex-1"
                      />
                      <span className="text-xs text-slate-600 w-10">{opacity}%</span>
                    </div>
                  </div>
                </div>
              ) : comparison?.analysis_status === 'analyzing' ? (
                <div className="h-48 flex flex-col items-center justify-center text-slate-500">
                  <Loader2 className="w-8 h-8 animate-spin mb-2" />
                  <p className="text-sm">{t('status_analyzing')}</p>
                </div>
              ) : (
                <div className="h-48 flex flex-col items-center justify-center text-slate-400 border-2 border-dashed rounded-lg">
                  <Layers className="w-8 h-8 mb-2" />
                  <p className="text-sm">{t('upload_both_drawings')}</p>
                  <p className="text-xs">{t('then_click')} "{t('start_smartrevision')}"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Analyze Button */}
        <div className="mt-6 flex flex-col items-center gap-4">
          {/* Format Mismatch Warning */}
          {comparison?.format_mismatch && comparison?.drawing_a_base64 && comparison?.drawing_b_base64 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800" data-testid="format-mismatch-warning">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div className="text-sm">
                <span className="font-medium">Attenzione:</span> I formati dei disegni sono diversi
                {comparison?.drawing_a_dims && comparison?.drawing_b_dims && (
                  <span className="text-amber-600 ml-1">
                    ({comparison.drawing_a_dims.width}x{comparison.drawing_a_dims.height} vs {comparison.drawing_b_dims.width}x{comparison.drawing_b_dims.height})
                  </span>
                )}
              </div>
            </div>
          )}
          
          <Button
            size="lg"
            onClick={() => handleAnalyze(false)}
            disabled={!comparison?.drawing_a_base64 || !comparison?.drawing_b_base64 || analyzing}
            className="px-8 bg-purple-600 hover:bg-purple-700"
            data-testid="analyze-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                SmartRevision...
              </>
            ) : (
              <>
                <Layers className="w-4 h-4 mr-2" />
                {t('start_smartrevision')}
              </>
            )}
          </Button>
        </div>
        
        {/* Guided View Comparison Section */}
        {comparison?.drawing_a_base64 && comparison?.drawing_b_base64 && (
          <GuidedViewComparison
            comparison={comparison}
            drawingAName={drawingAName}
            drawingBName={drawingBName}
            onAnalyzeViews={handleGuidedAnalysis}
            analyzing={analyzingGuided}
            userCredits={availableCredits}
            forceExpanded={forceExpandGuided}
          />
        )}
        
        {/* Guided View Results - Highlighted Section */}
        {guidedResults && (
          <Card className="mt-6 border-2 border-purple-400 bg-purple-50/50 shadow-lg" data-testid="guided-results">
            <CardHeader className="pb-3 bg-purple-100/50">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="p-2 bg-purple-600 rounded-full">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-purple-800">
                  {t('guided_results_title') || 'Risultati Comparazione Guidata'}
                </span>
                <span className="text-xs bg-purple-600 text-white px-3 py-1 rounded-full ml-auto">
                  {guidedResults.total_differences} {t('differences_found') || 'differenze'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {/* Info banner pointing to overlay */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Eye className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  I cerchi delle differenze sono visibili nella sezione <strong>"Differenze"</strong> in alto.
                  <button 
                    onClick={() => {
                      const overlayCard = document.querySelector('[data-testid="overlay-card"]');
                      overlayCard?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      handleOpenEditor();
                    }}
                    className="ml-2 text-blue-600 underline hover:text-blue-800 font-medium"
                  >
                    Apri editor fullscreen
                  </button>
                </p>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span>{t('views_analyzed') || 'Viste analizzate'}: <strong className="text-purple-700">{guidedResults.views_analyzed}</strong></span>
                <span className="text-slate-300">|</span>
                <span>{t('credits_consumed') || 'Crediti utilizzati'}: <strong className="text-purple-700">{guidedResults.credits_used}</strong></span>
              </div>
              
              <div className="grid gap-3">
                {guidedResults.results?.map((result, index) => (
                  <div key={index} className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-slate-800">{result.view_name}</h4>
                      {result.differences?.visual?.length > 0 ? (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                          {result.differences.visual.length} {result.differences.visual.length === 1 ? 'differenza' : 'differenze'}
                        </span>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                          Nessuna differenza
                        </span>
                      )}
                    </div>
                    
                    {result.differences?.visual?.length > 0 && result.differences.semantic?.length > 0 && (
                      <ul className="text-sm text-slate-600 list-disc ml-4 mt-2">
                        {result.differences.semantic.slice(0, 3).map((diff, i) => (
                          <li key={i}>{diff.description || diff.type}</li>
                        ))}
                        {result.differences.semantic.length > 3 && (
                          <li className="text-slate-400">...e altre {result.differences.semantic.length - 3}</li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* AI Plan Notice */}
        {user?.plan?.toUpperCase() !== 'AI' && user?.plan?.toUpperCase() !== 'AI_UNLIMITED' && user?.plan?.toUpperCase() !== 'AI_TRIAL' && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
            <AlertCircle className="w-5 h-5 inline mr-2 text-amber-600" />
            <span className="text-amber-800">
              SmartRevision richiede un piano AI (10 crediti per analisi).
              <Button variant="link" className="text-amber-700 underline ml-1" onClick={() => navigate('/subscription')}>
                Effettua l'upgrade
              </Button>
            </span>
          </div>
        )}
        
        {/* Differences List */}
        {differences && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Legend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('legend')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-slate-500 mb-3">
                  {t('legend_desc') || 'Le differenze sono evidenziate con cerchi/ellissi colorati per mantenere leggibili i valori originali.'}
                </p>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-red-500 rounded-full" />
                  <span className="text-sm">{t('removed')} ({t('removed_desc').replace('{oldRev}', drawingAName).replace('{newRev}', drawingBName)})</span>
                  <span className="ml-auto text-sm font-medium text-red-600">
                    {differences.stats?.removed || 0}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-green-500 rounded-full" />
                  <span className="text-sm">{t('added')} ({t('added_desc').replace('{oldRev}', drawingAName).replace('{newRev}', drawingBName)})</span>
                  <span className="ml-auto text-sm font-medium text-green-600">
                    {differences.stats?.added || 0}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-yellow-500 rounded-full" />
                  <span className="text-sm">{t('modified')}</span>
                  <span className="ml-auto text-sm font-medium text-yellow-600">
                    {differences.stats?.modified || 0}
                  </span>
                </div>
                
                {differences.summary && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-600">
                      <strong>{t('summary')}</strong> {differences.summary}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Semantic Differences */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('differences_list')}</CardTitle>
              </CardHeader>
              <CardContent>
                {differences.semantic && differences.semantic.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {differences.semantic.map((diff, i) => (
                      <div 
                        key={i}
                        className={`
                          p-3 rounded-lg border-l-4
                          ${diff.type === 'rimosso' ? 'bg-red-50 border-red-500' : ''}
                          ${diff.type === 'aggiunto' ? 'bg-green-50 border-green-500' : ''}
                          ${diff.type === 'modificato' ? 'bg-yellow-50 border-yellow-500' : ''}
                        `}
                      >
                        <div className="flex items-start gap-2">
                          {diff.type === 'rimosso' && <XCircle className="w-4 h-4 text-red-500 mt-0.5" />}
                          {diff.type === 'aggiunto' && <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />}
                          {diff.type === 'modificato' && <MinusCircle className="w-4 h-4 text-yellow-500 mt-0.5" />}
                          <div>
                            <p className="text-sm font-medium">{diff.description}</p>
                            {diff.details && (
                              <p className="text-xs text-slate-500 mt-1">{diff.details}</p>
                            )}
                            {diff.importance && (
                              <span className={`
                                text-xs px-1.5 py-0.5 rounded mt-1 inline-block
                                ${diff.importance === 'alta' || diff.importance === 'high' ? 'bg-red-100 text-red-700' : ''}
                                ${diff.importance === 'media' || diff.importance === 'medium' ? 'bg-amber-100 text-amber-700' : ''}
                                ${diff.importance === 'bassa' || diff.importance === 'low' ? 'bg-slate-100 text-slate-600' : ''}
                              `}>
                                {t('importance')} {
                                  diff.importance === 'alta' || diff.importance === 'high' ? t('importance_high') :
                                  diff.importance === 'media' || diff.importance === 'medium' ? t('importance_medium') :
                                  t('importance_low')
                                }
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    {differences.stats?.total > 0 
                      ? t('visual_diffs_detected')
                      : t('no_differences') || 'Nessuna differenza rilevata.'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Credits Used */}
        {comparison?.credits_used > 0 && (
          <p className="text-center text-sm text-slate-500 mt-4">
            {t('credits_used')} {comparison.credits_used}
          </p>
        )}
      </main>
      
      {/* Footer with Terms/Privacy links */}
      <Footer />
      
      {/* SmartRevision Tutorial */}
      <TutorialOverlay
        tutorialId="smartrevision_intro"
        steps={getTutorialSteps(t)}
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={() => setShowTutorial(false)}
        token={token}
        startStep={tutorialStartStep}
      />
      
      {/* Trial Upgrade Modal */}
      <TrialUpgradeModal 
        isOpen={trialModalOpen}
        onClose={() => setTrialModalOpen(false)}
        type={trialModalType}
      />
    </div>
  );
};

export default ComparePage;
